export type NormalizedGameData = {
    matchNum: number;
    position: string;
    ratingChange: number;
    seasonPoints: number;
    currentRating: number;
    bounce: number;
    date: number;
}

export type SummaryData = {
    total_rounds: number;
    rounds_per_day: number;
    current_rating: number;
    highest_rating: number;
    mean_points_gained_per_round: number;
    last_twenty_rating: number;
    total_season_points: number;
    season_points_per_round: number;
    current_mp_level: number;
    rounds_per_post_track_reward: number;
    rounds_by_date: GroupedRounds;
    rounds_by_hour: GroupedRounds;
    rounds_by_day_of_week: GroupedRounds;
    last_round: number;
    grouped_rating_changes: RatingChangeGroup;
    bounce_back_percentage: number;
    most_rounds_for_day: [string, NormalizedGameData[]];
    rounds_by_hour_advanced: AdvancedGroupedRound[];
    rounds_by_day_of_week_advanced: AdvancedGroupedRound[];
    longest_green_streak: StreakMeta;
    longest_red_streak: StreakMeta;
    longest_non_green_streak: StreakMeta;
    longest_non_red_streak: StreakMeta;
    longest_points_gained_streak: StreakMeta;
    longest_points_lost_streak: StreakMeta;
    current_streak: StreakMeta;
}

export type MatchSummaryData = {
    best_records: HeadToHeadRecord[];
    worst_records: HeadToHeadRecord[];
    most_played: HeadToHeadRecord[];
};

export type GroupedRounds = {
    [x: string]: NormalizedGameData[];
}

export type RatingChangeGroup = {
    [x: string]: RatingChange;
}

export type RatingChange = {
    total: number;
    percent: number;
}

export type AdvancedGroupedRound = {
    count: number;
    percent_of_total: number;
    total_change: number;
    average_change: number;
    gain_percent: number;
    even_percent: number;
    loss_percent: number;
};

export type StreakMeta = {
    len: number;
    includesZero: boolean;
    points_gained: number;
    start_match_num: number;
    end_match_num: number;
    start_date: number;
    end_date: number;
};

export type PlainObject = {
    [x: string]: any;
};

export type NormalizedMatchScoreCard = {
    [hole: string]: number;
};


export type NormalizedMatchPlayerData = {
    pID: string;
    name: string;
    status: string;
    icon: number;        // player icon index, see player_icons.ts
    iconBorder: number;  // icon border key (`b` in match data)
    totalScore: number;
    scoreCard: NormalizedMatchScoreCard;
};


export type NormalizedMatchData = {
    cardSize: number;
    timestamp: number;
    winners: string[];
    layout: string[];
    playerData: NormalizedMatchPlayerData[];
};

export type HeadToHeadRecord = {
    name: string;
    pID: string;
    timesPlayed: number;
    strokeDiff: number;
    w: number;
    t: number;
    l: number;
};

export type HeadToHeadRecords = {
    [pID: string]: HeadToHeadRecord;
};

export type HoleData = {
    shorthand: string;
    fullName: string;
    timesPlayed: number;
    scores: number[];
    par: number;
    elite: number;
    field_avg: number;
    difficulty: HoleDifficulty | PlainObject;
};

export type HoleMapping = {
    [hole: string]: HoleData;
};

export type HoleDifficulty = {
    albatross: number;
    average: number;
    birdie: number;
    bogey: number;
    cappedAverage: number;
    doubleBogey: number;
    eagle: number;
    eventCount: number;
    par: number;
    worse: number;
    holeName: string;
};

export type HoleDifficulties = {
    [holeName: string]: HoleDifficulty;
};

export type HoleSummaryData = {
    best_holes: HoleData[];
    worst_holes: HoleData[];
    overall_vs_field: number;
    count_better_than_par: number;
    count_worse_than_par: number;
    count_better_than_avg: number;
    count_worse_than_avg: number;
    count_better_than_elite: number;
    count_worse_than_elite: number;
};

export type CourseData = {
    name: string;
    shortname: string;
    ctv_order: number;
    par: number;
    elite: number;
    elite_score: number;
    field_avg: number;
    field_score: number;
    my_avg: number;
    my_score: number;
    me_vs_elite: number;
    me_vs_field: number;
};

export type CourseSummaryData = {
    [shortname: string]: CourseData;
}

export type CardData = {
    size: number;
    win: boolean;
    draw: boolean;
    loss: boolean;
    match_wins: number;
    match_draws: number;
    match_losses: number;
};

export type CardAndMatchSummaryData = {
    card_2_wins: number;
    card_2_win_percent: number;
    card_2_draws: number;
    card_2_draw_percent: number;
    card_2_losses: number;
    card_2_loss_percent: number;

    card_3_wins: number;
    card_3_win_percent: number;
    card_3_draws: number;
    card_3_draw_percent: number;
    card_3_losses: number;
    card_3_loss_percent: number;

    card_4_wins: number;
    card_4_win_percent: number;
    card_4_draws: number;
    card_4_draw_percent: number;
    card_4_losses: number;
    card_4_loss_percent: number;
};

export type CardSummaryData = {
    total_rounds: number;
    total_matches: number;
    total_unique_matches: number;

    average_card_size: number;

    overall_card_wins: number,
    overall_card_draws: number,
    overall_card_losses: number,
    overall_card_wins_percent: number,
    overall_card_draws_percent: number,
    overall_card_losses_percent: number,

    overall_match_wins: number,
    overall_match_draws: number,
    overall_match_losses: number,
    overall_match_wins_percent: number,
    overall_match_draws_percent: number,
    overall_match_losses_percent: number,

    card_2_total: number;
    card_3_total: number;
    card_4_total: number;

    card_2_percent: number;
    card_3_percent: number;
    card_4_percent: number;

    card_2_matches_total: number;
    card_3_matches_total: number;
    card_4_matches_total: number;

    card: CardAndMatchSummaryData;
    match: CardAndMatchSummaryData;
};


export interface EventFormData {
    eventID: string;
    createdBy?: string; // uid of the creator; missing on events made before owners were tracked
    banner: number;
    bgImageLockedName: string;
    bgImageName: string;
    customReward?: DiscProperties;
    customRewards: {
        eventRewardCoins: number;
    };
    divRestrict: boolean;
    division: number;
    eventAccess: number;
    eventEndTime: string;
    eventStartTime: string;
    eventFixedSchedule: boolean;
    eventInfoURL?: string;
    eventName: string;
    eventVisibility: boolean;
    minVersion: number;
    paidEvent: boolean;
    paidEventTier: number;
    participationReward?: number;
    rounds: EventRound[];
    // Players to invite while the event is pending; written to events/inviteAccess when it goes live as an Invite event
    invitedPlayers?: string[];
}

export interface DiscProperties {
    cS: boolean;
    colorsName: string;
    discProperties: number[];
    favoriteDiscTier: number;
    mouldName: number;
    plasticReflectivity: number;
    stampReflectivity: number;
    stampVariant: number;
    stockVariant: number;
}

export interface EventRound {
    courseID: string;
    courseName: string;
    discBag: number;
    holes: EventHole[];
    locked: boolean;
    reverse: boolean;
    windSeed: number;
    customCourse: boolean;
};

export interface EventHole {
    holeName: string;
    pS: number;
    par: number;
}

export class EventModel {
    eventId: string;
    round: Number;
    isPublic: boolean;
    locked: boolean;

    constructor(eventId: string = '', round: Number = 0, isPublic: boolean = true, locked: boolean = false) {
        this.eventId = eventId;
        this.round = round;
        this.isPublic = isPublic;
        this.locked = locked;
    }
}

// Model used for move event from 
export class EventPendingModel {
    eventId: string;

    constructor(eventId: string = '') {
        this.eventId = eventId;
    }
}

export enum EventAccess {
    Private = 0,
    Public = 1,
    Archive = 2,
    Pending = 3,
    Invite = 4
}

// Matches the EventBanner enum in the game client
export enum EventBanner {
    FirstRound = 0,
    PaidEvent = 3,
    CustomBackground = 4, // client loads "<eventID>.png" as the event panel background
}

export const generateRandomWindSeed = () => Math.floor(Math.random() * 1000);

const HOUR_MS = 60 * 60 * 1000;

// Formats a timestamp as a UTC value for the datetime-local inputs ("YYYY-MM-DDTHH:mm")
const toInputTime = (timestamp: number) => new Date(timestamp).toISOString().slice(0, 16);

// Built when the form opens so the default start is always 24h from now,
// leaving time to fill in the form before the start time passes
export const createDefaultEventFormData = (): EventFormData => ({
    eventID: '',
    banner: EventBanner.FirstRound,
    bgImageLockedName: '',
    bgImageName: '',
    customReward: {
        cS: true,
        colorsName: 'Black1',
        discProperties: [5, 2],
        favoriteDiscTier: 0,
        mouldName: 40,
        plasticReflectivity: 0,
        stampReflectivity: 0,
        stampVariant: 0,
        stockVariant: 0,
    },
    customRewards: {
        eventRewardCoins: 0,
    },
    divRestrict: false,
    division: 2,
    eventAccess: 0,
    eventEndTime: toInputTime(Date.now() + 8 * 24 * HOUR_MS),
    eventStartTime: toInputTime(Date.now() + 24 * HOUR_MS),
    eventFixedSchedule: false,
    eventInfoURL: '',
    eventName: '',
    eventVisibility: true,
    minVersion: 0,
    paidEvent: false,
    paidEventTier: 0,
    participationReward: undefined,
    rounds: []
});
